import { apiCustomer } from "@/api"
import { checkoutAttribution } from "@/domain/attribution/attribution"
import { useNavigate } from "react-router-dom"
import { activeOrdersAtom } from "@/atoms/active-orders"
import {
  availabilitiesAtom,
  basicInfoAtom,
  categoriesAtom,
  deliveryServiceAtom,
  itemOffersAtom,
  itemsAtom,
  merchantStatusAtom,
  optionGroupsAtom,
  takeoutServiceAtom,
} from "@/atoms"
import { indoorCategoriesAtom, indoorItemOffersAtom, indoorServiceAtom } from "@/atoms/indoor"
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config"
import { Button, ButtonLink, PageContainer } from "@/components/ui"
import { formatBRL } from "@/lib/format-brl"
import { primeEffectSound } from "@/lib/effect-sound"
import { useSeasonalEffects } from "@/hooks/use-seasonal-effects"
import { StatusOverlay, showToast } from "@/components/feedback"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { useCart } from "@/hooks/use-cart"
import { useMinimumOrder } from "@/hooks/use-minimum-order"
import { refreshActiveOrders } from "@/hooks/use-active-orders"
import { refreshApiData } from "@/hooks/use-api-polling"
import { delay } from "@/lib"
import { buildOrderRequest } from "@/lib/build-order-request"
import { channelCatalogSnapshot } from "@/lib/catalog-channel"
import {
  isServiceOpen,
  isServiceOpenInCatalog,
  serviceTypeLabel,
} from "@/lib/is-service-open"
import { isStoredCartExpired } from "@/lib/cart-local-storage"
import { formatRemovedLineLabel } from "@/lib/prune-cart-against-catalog"
import {
  revalidateCartAgainstCatalog,
  type CatalogSnapshot,
} from "@/lib/revalidate-checkout-catalog"
import { describeCheckoutError, genericRejectionMessage } from "@/lib/checkout-errors"
import { checkoutConfirmation, isBagCheckoutFailure, isTerminalCheckoutFailure } from "@/lib/checkout-outcome"
import { waitForCheckoutResolution, type PixQrData } from "@/lib/checkout-saga"
import { applyPriceReviewToCart } from "@/lib/apply-price-review"
import { PriceReviewSheet } from "@/components/cart/PriceReviewSheet"
import type { PriceReview } from "@/types/external"
import { errorMessage } from "@/lib/i18n/error-messages"
import { ensureCompanyForCurrentHost } from "@/lib/ensure-company"
import { renewAccountTokenIfDue } from "@/lib/renew-account-token"
import {
  checkoutContextAttributes,
  describeCart,
  describeOrderRequest,
  describeService,
  failSpan,
  httpErrorAttributes,
  jsonAttr,
  markCheckoutFail,
  markCheckoutOutcome,
  normalizeError,
  recordAttrs,
  recordDetail,
  recordException,
  setCheckoutAttrs,
  summarizeCart,
  withCheckoutSpan,
} from "@/lib/otel"
import { getStoredCompany } from "@/lib/company-storage"
import { holdAppReload } from "@/lib/app-reload-guard"
import { selectDeliveryPolygon } from "@/pages/cart/checkout/components/service-area"
import { trackMetaPurchase } from "@/lib/meta-pixel"
import { recordOrderConversion } from "@/lib/record-order-conversion"
import { Address, Cart, PaymentMethod, Service, ServiceType } from "@/types"
import { Discount } from "@/types/external"
import type { OrderTiming } from "@/types/external/order-request"
import { formatArrivalCommitment, formatScheduledFor } from "@/lib/scheduling/format"
import { getTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import type { SchedulingSlot } from "@/lib/scheduling/types"
import Profile from "@/types/profile"
import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { useAtomValue } from "jotai"
import { useEffect, useRef, useState } from "react"
import DuplicateOrderWarningModal from "./DuplicateOrderWarningModal"
import { PixQrOverlay } from "./PixQrOverlay"

/** Refusals that mean "this bag does not fit that slot" (§2A.6, UC5). */
const ITEM_WINDOW_CODES = new Set([
  "ORD-CHK-2001",
  "ORD-CHK-4002",
  "ORD-CRT-2002",
  "ITEM_UNAVAILABLE",
])

function channelClosedMessage(serviceType: ServiceType): string {
  const label = serviceTypeLabel(serviceType)
  return `O canal de ${label} está indisponível no momento. Volte quando ele abrir!`
}

function pickServiceForType(
  serviceType: ServiceType,
  services: { delivery: Service | null; takeout: Service | null; indoor: Service | null },
): Service | null {
  if (serviceType === "DELIVERY") return services.delivery
  if (serviceType === "TAKEOUT") return services.takeout
  return services.indoor
}

export default function FinishOrderButton({
  profile,
  serviceType,
  orderTiming = "INSTANT",
  scheduledSlot = null,
  scheduledArrival = null,
  paymentMethod,
  methodInfo,
  paymentMethodId,
  specialInstructions,
  servicePrice,
  pixServiceFee = 0,
  couponDiscounts = [],
  changeFor,
  cart,
  address,
  loyaltyPoints,
  benefitDiscounts,
  isTableOrder = false,
  disabled = false,
  tableId,
  tableExternalCode,
  notifyStatusWhatsapp = true,
  onOrderPlaced,
  onOrderConfirmed,
  onPixClosed,
  onDeliveryFeeChanged,
  buttonLabel,
  buttonClassName,
  containerClassName,
}: {
  profile: Profile
  serviceType: ServiceType | undefined
  /** Modality of the order; SCHEDULED skips the "open right now" guards. */
  orderTiming?: OrderTiming
  /** Chosen slot for a scheduled order. */
  scheduledSlot?: SchedulingSlot | null
  /** Arrival window promised to the customer; full model only (D3). */
  scheduledArrival?: { start: string; end: string } | null
  paymentMethod: PaymentMethod | undefined
  methodInfo?: string
  paymentMethodId?: string
  specialInstructions: string
  servicePrice: number
  /** PIX Online service fee (BRL) when pass-through is enabled; 0 otherwise. */
  pixServiceFee?: number
  /** One discount per coupon allocation coupon-service returned for this bag (contract C6). */
  couponDiscounts?: Discount[]
  changeFor: number
  cart: Cart
  address?: Address
  loyaltyPoints: number
  benefitDiscounts?: Discount[]
  /** True for dine-in/table orders (QR code entry): no paymentMethod required, INDOOR payload built by buildOrderRequest. */
  isTableOrder?: boolean
  /** Holds the button while the discounts are still being evaluated for this bag. */
  disabled?: boolean
  /** Table UUID from the QR slug; forms the indoor `place` wire value. */
  tableId?: string
  /** Table's PDV/POS external code, if configured; combined as `${tableId}|${tableExternalCode}`. */
  tableExternalCode?: string
  /** The customer's "Receber atualizações do meu pedido pelo WhatsApp" box; sent on the order, on by default. */
  notifyStatusWhatsapp?: boolean
  /** Called once checkout accepted the order (it has an id), before the saga settles; never awaited, a throw is ignored. */
  onOrderPlaced?: (orderId: string) => void
  /** `scheduledSummary` is the chosen slot in words; null for an immediate order. */
  onOrderConfirmed?: (orderId: string, scheduledSummary: string | null) => void
  onPixClosed?: (orderId: string) => void
  /** Called with the fee (BRL) the core quoted when it refused a changed delivery fee. */
  onDeliveryFeeChanged?: (fee: number) => void
  buttonLabel?: string
  buttonClassName?: string
  containerClassName?: string
}) {
  const [loading, setLoading] = useState(false)
  const [orderId, setOrderId] = useState("")
  const [orderDone, setOrderDone] = useState(false)
  const [orderFail, setOrderFail] = useState(false)
  const [orderFailMessage, setOrderFailMessage] = useState("")
  const [loadingMessage, setLoadingMessage] = useState("Enviando seu pedido...")
  const [invalidOrder, setInvalidOrder] = useState(false)
  const [bagFailure, setBagFailure] = useState(false)
  const [scheduledItemConflict, setScheduledItemConflict] = useState(false)
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false)
  const hasConfirmedDuplicate = useRef(false)
  const [pixQrData, setPixQrData] = useState<PixQrData | null>(null)
  const [showPixOverlay, setShowPixOverlay] = useState(false)
  const pixOrderIdRef = useRef("")
  const showPixOverlayRef = useRef(false)
  const [priceReview, setPriceReview] = useState<PriceReview | null>(null)
  const [priceReviewSubmitting, setPriceReviewSubmitting] = useState(false)
  const priceReviewRef = useRef<PriceReview | null>(null)
  const priceReviewOrderIdRef = useRef("")
  const navigate = useNavigate()
  const seasonalSound = useSeasonalEffects("orderConfirmed")?.sound
  const merchantStatus = useAtomValue(merchantStatusAtom)
  const tenantTimezone = useAtomValue(tenantTimezoneAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)
  const takeoutService = useAtomValue(takeoutServiceAtom)
  const indoorService = useAtomValue(indoorServiceAtom)
  const { isSyncing: isSyncPending, flush, clearAfterCheckout } = useOptimisticCart()
  const { updateCart } = useCart()
  const deliveryCategories = useAtomValue(categoriesAtom)
  const indoorCategories = useAtomValue(indoorCategoriesAtom)
  const orderChannel: ServiceType = isTableOrder ? "INDOOR" : (serviceType ?? "DELIVERY")
  const sellsIndoorMenu = orderChannel === "INDOOR"
  const categories = sellsIndoorMenu ? indoorCategories : deliveryCategories
  const deliveryItemOffers = useAtomValue(itemOffersAtom)
  const indoorItemOffers = useAtomValue(indoorItemOffersAtom)
  const items = useAtomValue(itemsAtom)
  const optionGroups = useAtomValue(optionGroupsAtom)
  const availabilities = useAtomValue(availabilitiesAtom)
  const polledCatalog: CatalogSnapshot = {
    itemOffers: sellsIndoorMenu ? indoorItemOffers : deliveryItemOffers,
    optionGroups,
    categories,
    availabilities,
    items,
  }
  const basicInfo = useAtomValue(basicInfoAtom)
  const activeOrders = useAtomValue(activeOrdersAtom)
  const minimumOrder = useMinimumOrder()
  const isBelowMinimum = minimumOrder.isBelowMinimum(cart.totalPrice)

  const isScheduled = orderTiming === "SCHEDULED" && scheduledSlot !== null

  const scheduledSummary = !isScheduled
    ? null
    : scheduledArrival && serviceType
      ? formatArrivalCommitment(
          { arrivalStart: scheduledArrival.start, arrivalEnd: scheduledArrival.end },
          serviceType,
          getTenantTimezone(),
        )
      : scheduledSlot
        ? formatScheduledFor(scheduledSlot.readyAt, new Date(), getTenantTimezone())
        : null

  const failureIsTerminal = isTerminalCheckoutFailure(invalidOrder, orderFailMessage)
  const confirmation = checkoutConfirmation(scheduledSummary)

  const holdsReload = loading || showPixOverlay || priceReview !== null
  useEffect(() => {
    if (!holdsReload) return
    return holdAppReload()
  }, [holdsReload])

  async function handleFailureAction() {
    if (bagFailure) {
      setOrderFail(false)
      setOrderFailMessage("")
      setBagFailure(false)
      navigate("/cart")
      return
    }
    if (!failureIsTerminal) {
      setOrderFail(false)
      setOrderFailMessage("")
      await handleClick()
      return
    }

    if (invalidOrder) {
      await clearAfterCheckout()
      setInvalidOrder(false)
    }
    window.location.href =
      isTableOrder && tableId ? `/?table=${encodeURIComponent(tableId)}` : "/"
  }

  async function handleClick() {
    if (!serviceType || (!isTableOrder && !paymentMethod)) return
    if (orderTiming === "SCHEDULED" && !scheduledSlot) return
    if (seasonalSound) primeEffectSound(seasonalSound)

    await withCheckoutSpan(
      "checkout.submit",
      {
        merchantId: getStoredCompany()?.id,
        serviceType,
        orderTiming,
        paymentMethod,
        itemCount: cart.items.length,
        cartSummary: summarizeCart(cart),
      },
      async (checkoutSpan) => {
    recordAttrs(checkoutSpan, {
      "checkout.cart_total": cart.totalPrice,
      "checkout.duplicate_confirmed": hasConfirmedDuplicate.current,
    })
    recordDetail(checkoutSpan, "checkout.cart", describeCart(cart))
    recordAttrs(
      checkoutSpan,
      checkoutContextAttributes({
        isTableOrder,
        tableId,
        tableExternalCode,
        merchantStatus,
        minOrderValue: basicInfo?.minOrderValue?.value,
        activeOrders: activeOrders.length,
        couponDiscounts,
        loyaltyPoints,
        benefitDiscounts,
        servicePrice,
        pixServiceFee,
        changeFor,
        paymentMethodId,
        methodInfo,
        specialInstructions,
        scheduledSlot,
        scheduledArrival,
        address,
      }),
    )

    if (isBelowMinimum) {
      const msg = `O pedido mínimo da loja é ${formatBRL(minimumOrder.minimum)}`
      markCheckoutFail(checkoutSpan, "min_order", msg)
      setOrderFailMessage(msg)
      setOrderFail(true)
      return
    }

    if (activeOrders.length > 0 && !hasConfirmedDuplicate.current) {
      markCheckoutOutcome(checkoutSpan, "duplicate_warning", {
        "checkout.active_order_id": activeOrders[0].id,
        "checkout.active_orders": activeOrders.length,
      })
      setShowDuplicateWarning(true)
      return
    }

    setOrderFail(false)
    setOrderFailMessage("")
    setBagFailure(false)
    setLoading(true)
    setLoadingMessage("Enviando seu pedido...")

    await flush()

    if (isStoredCartExpired()) {
      markCheckoutFail(checkoutSpan, "cart_expired", errorMessage("cartNotFound"))
      showToast.warning(errorMessage("cartNotFound"), { title: "Sacola expirada" })
      await clearAfterCheckout()
      setLoading(false)
      return
    }

    const channelService = pickServiceForType(serviceType, {
      delivery: deliveryService,
      takeout: takeoutService,
      indoor: indoorService,
    })
    const channelClosed =
      isScheduled ? false : merchantStatus === "UNAVAILABLE" || !isServiceOpen(channelService, new Date(), tenantTimezone)
    if (channelClosed) {
      const msg = channelClosedMessage(serviceType)
      recordDetail(checkoutSpan, "checkout.service", describeService(channelService))
      markCheckoutFail(checkoutSpan, "channel_closed", msg)
      setOrderFailMessage(msg)
      setOrderFail(true)
      setLoading(false)
      return
    }

    const freshCatalog = await refreshApiData()
    recordAttrs(checkoutSpan, { "checkout.catalog_source": freshCatalog ? "refetch" : "poll" })
    if (freshCatalog && !isScheduled && !isServiceOpenInCatalog(freshCatalog, serviceType, new Date(), tenantTimezone)) {
      const msg = channelClosedMessage(serviceType)
      recordDetail(
        checkoutSpan,
        "checkout.service",
        describeService(freshCatalog.services.find((service) => service.serviceType === serviceType) ?? null),
      )
      recordAttrs(checkoutSpan, { "checkout.merchant_status_fresh": freshCatalog.status })
      markCheckoutFail(checkoutSpan, "channel_closed_revalidate", msg)
      setOrderFailMessage(msg)
      setOrderFail(true)
      setLoading(false)
      return
    }

    const freshChannelCatalog = freshCatalog ? channelCatalogSnapshot(freshCatalog, orderChannel) : null
    if (freshCatalog && !freshChannelCatalog) {
      const msg = "Este cardápio não está disponível no momento."
      markCheckoutFail(checkoutSpan, "channel_without_menu", msg)
      setOrderFailMessage(msg)
      setOrderFail(true)
      setLoading(false)
      return
    }

    const revalidation = revalidateCartAgainstCatalog(cart, freshChannelCatalog ?? polledCatalog)
    recordDetail(checkoutSpan, "checkout.revalidation", revalidation.lines)
    checkoutSpan.addEvent("checkout.revalidated", {
      "checkout.catalog_source": freshCatalog ? "refetch" : "poll",
      "checkout.prices_moved": revalidation.pricesMoved,
      "checkout.unavailable_count": revalidation.unavailableItems.length,
    })

    if (!isScheduled && revalidation.unavailableItems.length > 0) {
      updateCart(revalidation.refreshedCart)
      const unavailableLines = revalidation.lines.filter((line) => line.reason !== null)
      recordAttrs(checkoutSpan, {
        "checkout.unavailable_reasons": [...new Set(unavailableLines.map((line) => line.reason))].join(","),
        "checkout.unavailable_option_names": [
          ...new Set(
            unavailableLines
              .map((line) => line.optionName)
              .filter((name): name is string => !!name?.trim()),
          ),
        ].join(","),
      })
      recordDetail(checkoutSpan, "checkout.unavailable_lines", unavailableLines)
      const labels = unavailableLines.map((line) => formatRemovedLineLabel(line))
      const names = labels.join(", ")
      const msg =
        labels.length === 1
          ? `O item "${names}" está indisponível no momento. Revise sua sacola para continuar.`
          : `Alguns itens estão indisponíveis no momento (${names}). Revise sua sacola para continuar.`
      markCheckoutFail(checkoutSpan, "items_unavailable", msg, "ITEM_UNAVAILABLE")
      setBagFailure(true)
      setOrderFailMessage(msg)
      setOrderFail(true)
      setLoading(false)
      return
    }

    if (revalidation.pricesMoved) {
      updateCart(revalidation.refreshedCart)
      recordDetail(
        checkoutSpan,
        "checkout.price_drift",
        revalidation.lines.filter((line) => line.previousTotal !== line.refreshedTotal),
      )
      markCheckoutFail(checkoutSpan, "stale_cart_prices", errorMessage("staleCart"))
      showToast.warning(errorMessage("staleCart"), { title: "Sua sacola foi atualizada" })
      setLoading(false)
      return
    }

    let confirmedOrderId: string | null
    try {
      const { company, healed, error: companyHealError } = await ensureCompanyForCurrentHost()
      if (healed) {
        console.info("[checkout] storefront company auto-healed before POST")
        checkoutSpan.addEvent("checkout.company_healed")
      }
      if (companyHealError && company) {
        console.warn("[checkout] company revalidate failed; using cached company", companyHealError)
      }

      const merchantId = company?.id
      if (!merchantId) {
        const msg = "Não conseguimos identificar a loja. Atualize a página e tente de novo."
        markCheckoutFail(checkoutSpan, "merchant_id_missing", msg)
        setOrderFailMessage(msg)
        setOrderFail(true)
        setLoading(false)
        return
      }
      setCheckoutAttrs(checkoutSpan, { merchantId })

      const orderRequest = buildOrderRequest({
        serviceType,
        orderTiming,
        scheduledSlot,
        scheduledArrival,
        paymentMethod,
        methodInfo,
        paymentMethodId,
        specialInstructions,
        servicePrice,
        pixServiceFee,
        couponDiscounts,
        changeFor,
        cart,
        address,
        loyaltyPoints,
        benefitDiscounts,
        profile,
        categories,
        merchantDocument: company.document,
        storeName: basicInfo?.name ?? "",
        tableId,
        tableExternalCode,
        notifyStatusWhatsapp,
        takeoutEstimateMinutes: basicInfo?.averagePreparationTime,
        deliveryEstimateMinutes:
          serviceType === "DELIVERY" && address && deliveryService
            ? selectDeliveryPolygon(
                address,
                deliveryService.serviceArea,
                basicInfo?.address,
                deliveryAreaConfig,
              )?.estimateDeliveryTime
            : undefined,
      })
      const attribution = checkoutAttribution(merchantId)
      if (attribution) orderRequest.attribution = attribution
      checkoutSpan.setAttribute("checkout.attribution_source", attribution?.utmSource ?? attribution?.referrerHost ?? "none")

      recordDetail(checkoutSpan, "checkout.order_request", describeOrderRequest(orderRequest))

      await renewAccountTokenIfDue()

      const response = await apiCustomer.post(`/carts/by-merchant/${merchantId}/checkout`, orderRequest)
      recordDetail(checkoutSpan, "checkout.checkout_response", response.data)

      const orderId = response.data?.id
      if (!orderId) {
        throw new Error("Pedido não retornou um ID")
      }

      confirmedOrderId = orderId
      setOrderId(orderId)
      recordAttrs(checkoutSpan, { "checkout.order_id": orderId })
      checkoutSpan.addEvent("checkout.posted")
      try {
        onOrderPlaced?.(orderId)
      } catch {
        checkoutSpan.addEvent("checkout.order_placed_callback_failed")
      }

      setLoadingMessage("Validando seu pedido...")

      const isPixOnline = paymentMethod === "PIX_ONLINE"
      let pixExpiresAt: string | undefined

      const checkoutResolution = await waitForCheckoutResolution(orderId, {
        isPixOnline,
        onPollError: (pollError, attempt) => {
          checkoutSpan.addEvent("checkout.saga_poll_error", {
            "checkout.poll_attempt": attempt,
            "checkout.fail_message": normalizeError(pollError).message.slice(0, 300),
            ...httpErrorAttributes(pollError),
          })
        },
        onPriceReview: (review) => {
          priceReviewOrderIdRef.current = orderId
          priceReviewRef.current = review
          setPriceReview(review)
          setLoading(false)
        },
        onPixQrReady: (data) => {
          pixExpiresAt = data.expiresAt
          setPixQrData(data)
          pixOrderIdRef.current = orderId
          showPixOverlayRef.current = true
          setShowPixOverlay(true)
          setLoading(false)
        },
      })

      if (checkoutResolution.saga) {
        const { saga } = checkoutResolution
        recordDetail(checkoutSpan, "checkout.saga", saga)
        recordAttrs(checkoutSpan, {
          "checkout.saga_polls": saga.polls,
          "checkout.saga_id": saga.sagaId,
          "checkout.saga_state": saga.state,
          "checkout.saga_outcome": saga.outcome,
          "checkout.saga_rejected_steps":
            saga.rejectedValidations.length > 0
              ? saga.rejectedValidations.map((validation) => validation.step).join(",")
              : undefined,
        })
      }

      if (priceReviewRef.current) {
        markCheckoutOutcome(checkoutSpan, "price_review", {
          "checkout.price_review": jsonAttr(priceReviewRef.current, 8000),
        })
        return
      }

      if (showPixOverlayRef.current && !checkoutResolution.success && !checkoutResolution.pixCancelled) {
        markCheckoutOutcome(checkoutSpan, "pix_pending", {
          ...(pixExpiresAt ? { "checkout.pix_expires_at": pixExpiresAt } : {}),
        })
        return
      }

      if (!checkoutResolution.success) {
        if (checkoutResolution.pixCancelled) {
          markCheckoutFail(checkoutSpan, "pix_cancelled", "PIX QR expirou antes do pagamento")
          return
        }

        const failMsg = checkoutResolution.timedOut
          ? errorMessage("checkoutTimeout")
          : checkoutResolution.failMessage || genericRejectionMessage()
        markCheckoutFail(
          checkoutSpan,
          checkoutResolution.timedOut ? "saga_timeout" : "saga_rejected",
          failMsg,
          checkoutResolution.failCode,
        )
        setScheduledItemConflict(
          isScheduled && ITEM_WINDOW_CODES.has(checkoutResolution.failCode ?? ""),
        )
        setBagFailure(isBagCheckoutFailure(checkoutResolution.failCode))
        setOrderFailMessage(failMsg)
        setOrderFail(true)
        setLoading(false)
        return
      }

      trackMetaPurchase({
        cart,
        value: orderRequest.total.orderAmount.value,
        orderId: confirmedOrderId ?? orderId,
      })

    } catch (error) {
      const failure = describeCheckoutError(error)
      failSpan(checkoutSpan, error, failure.code)
      markCheckoutFail(checkoutSpan, "post_or_exception", failure.message, failure.code)
      if (failure.invalidOrder) {
        setInvalidOrder(true)
      }
      setBagFailure(isBagCheckoutFailure(failure.code))
      if (failure.quotedDeliveryFee != null) onDeliveryFeeChanged?.(failure.quotedDeliveryFee)
      setOrderFailMessage(failure.message)
      setLoading(false)
      setOrderFail(true)
      return
    }

    markCheckoutOutcome(checkoutSpan, "confirmed")
    await delay(1000)
    setLoading(false)
    if (confirmedOrderId) recordOrderConversion(cart, confirmedOrderId)
    if (onOrderConfirmed && confirmedOrderId) {
      onOrderConfirmed(confirmedOrderId, scheduledSummary)
      await clearAfterCheckout()
      refreshActiveOrders()
      return
    }
    setOrderDone(true)
    await clearAfterCheckout()
    refreshActiveOrders()
      },
    )
  }

  async function handlePixConfirmed() {
    const currentOrderId = pixOrderIdRef.current || orderId
    showPixOverlayRef.current = false
    setShowPixOverlay(false)
    setPixQrData(null)
    await delay(1000)
    setLoading(false)
    if (currentOrderId) recordOrderConversion(cart, currentOrderId)
    if (onOrderConfirmed && currentOrderId) {
      onOrderConfirmed(currentOrderId, scheduledSummary)
      await clearAfterCheckout()
      refreshActiveOrders()
      return
    }
    setOrderId(currentOrderId)
    setOrderDone(true)
    await clearAfterCheckout()
    refreshActiveOrders()
  }

  async function handlePixClose() {
    const currentOrderId = pixOrderIdRef.current || orderId
    showPixOverlayRef.current = false
    setShowPixOverlay(false)
    setPixQrData(null)
    setLoading(false)
    onPixClosed?.(currentOrderId)
    await clearAfterCheckout()
    refreshActiveOrders()
  }

  /**
   * Both branches rewrite the bag with the merchant's prices. Accepting because
   * that is the total the customer agreed to; declining because sending them
   * back to a bag showing the old price would reproduce the same divergence on
   * the next attempt.
   */
  async function resolvePriceReview(accept: boolean) {
    const review = priceReviewRef.current
    if (!review || priceReviewSubmitting) return

    const reviewOrderId = priceReviewOrderIdRef.current

    setPriceReviewSubmitting(true)
    try {
      if (!reviewOrderId) return

      await apiCustomer.post(`/orders/${reviewOrderId}/price-review`, { accept })
      updateCart(applyPriceReviewToCart(cart, review))

      priceReviewRef.current = null
      setPriceReview(null)

      if (accept) {
        setLoadingMessage("Validando seu pedido...")
        setLoading(true)
        return
      }

      navigate("/cart")
    } catch (error) {
      recordException("checkout.price_review", error, {
        "checkout.order_id": reviewOrderId,
        "checkout.price_review_accept": accept,
        "checkout.price_review": jsonAttr(review, 8000),
        ...httpErrorAttributes(error),
      })
      showToast.error(errorMessage("genericRetry"))
    } finally {
      setPriceReviewSubmitting(false)
    }
  }

  return (
    <>
      <PriceReviewSheet
        open={priceReview !== null}
        review={priceReview}
        submitting={priceReviewSubmitting}
        onAccept={() => void resolvePriceReview(true)}
        onDecline={() => void resolvePriceReview(false)}
      />

      {showPixOverlay && pixQrData && (
        <PixQrOverlay
          open={showPixOverlay}
          orderId={pixOrderIdRef.current || orderId}
          qrCode={pixQrData.qrCode}
          qrCodeBase64={pixQrData.qrCodeBase64}
          expiresAt={pixQrData.expiresAt}
          onConfirmed={handlePixConfirmed}
          onClose={handlePixClose}
        />
      )}

      <StatusOverlay
        open={(loading || orderDone || orderFail) && !showPixOverlay}
        status={loading ? "loading" : orderDone ? "success" : "error"}
        title={loading ? loadingMessage : orderDone ? confirmation.title : "Opa, algo deu errado"}
        message={
          loading
            ? "Segura aí, é rapidinho."
            : orderDone
              ? confirmation.message
              : orderFailMessage || "Algo deu errado por aqui. Tente de novo em instantes."
        }
      >
        {loading ? null : orderDone ? (
          <ButtonLink to={`/profile/orders/${orderId}`} replace fullWidth>
            Acompanhar pedido
          </ButtonLink>
        ) : orderFail && scheduledItemConflict ? (
          <div className="flex w-full flex-col gap-2">
            <Button
              type="button"
              fullWidth
              onClick={() => {
                setOrderFail(false)
                setScheduledItemConflict(false)
                navigate("/cart/checkout/schedule")
              }}
            >
              Escolher outro horário
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => {
                setOrderFail(false)
                setScheduledItemConflict(false)
                navigate("/cart")
              }}
            >
              Remover o item da sacola
            </Button>
          </div>
        ) : orderFail ? (
          <div className="flex w-full flex-col gap-2">
            <Button type="button" fullWidth onClick={handleFailureAction}>
              {bagFailure
                ? "Revisar a sacola"
                : failureIsTerminal
                  ? "Voltar para o cardápio"
                  : "Tentar de novo"}
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => {
                setOrderFail(false)
                setOrderFailMessage("")
              }}
            >
              Fechar
            </Button>
          </div>
        ) : null}
      </StatusOverlay>

      {activeOrders.length > 0 && (
        <DuplicateOrderWarningModal
          open={showDuplicateWarning}
          onOpenChange={setShowDuplicateWarning}
          activeOrder={activeOrders[0]}
          onConfirm={() => {
            hasConfirmedDuplicate.current = true
            setShowDuplicateWarning(false)
            void handleClick()
          }}
        />
      )}

      <PageContainer gutter={containerClassName ? "none" : "container"} className={containerClassName}>
        <Button
          size="lg"
          fullWidth
          disabled={disabled || (!isTableOrder && !(serviceType && paymentMethod)) || isBelowMinimum}
          isLoading={isSyncPending}
          onClick={handleClick}
          className={buttonClassName}
        >
          {isSyncPending ? (
            "Salvando sacola..."
          ) : isBelowMinimum ? (
            `Pedido mínimo: ${formatBRL(minimumOrder.minimum)}`
          ) : (
            buttonLabel ?? "Confirmar pedido"
          )}
        </Button>
      </PageContainer>
    </>
  )
}



